import type {
  Lead,
  Rep,
  Roster,
  SessionUser,
  Paginated,
  DashboardStats,
  ActivityRecord,
  LeaderboardReport,
  LeaderboardSettings,
  ContentBundle,
  ProspectWorkspace,
  ProspectWorkspaceRecord,
  StatusEntry,
} from '@leadgenos/shared';

/** All requests are same-origin (dev proxies /api → API). Cookies carry the session. */
const BASE = '';

/**
 * "View as Rep" impersonation target. Managers/admins set this to a rep id; every request then
 * carries `X-View-As` so the server scopes data to that rep (server verifies the real user is a
 * manager/admin — the client header alone never escalates scope).
 */
let viewAsId: string | null = null;
export function setViewAs(id: string | null): void {
  viewAsId = id;
}

export class ApiError extends Error {
  status: number;
  constructor(status: number, message: string) {
    super(message);
    this.status = status;
  }
}

async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
  const headers: Record<string, string> = {};
  if (body) headers['Content-Type'] = 'application/json';
  if (viewAsId) headers['X-View-As'] = viewAsId;
  const res = await fetch(BASE + path, {
    method,
    credentials: 'include',
    headers: Object.keys(headers).length ? headers : undefined,
    body: body ? JSON.stringify(body) : undefined,
  });
  if (res.status === 204) return undefined as T;
  const text = await res.text();
  const data = text ? JSON.parse(text) : null;
  if (!res.ok) {
    throw new ApiError(res.status, (data && data.error) || `Request failed (${res.status})`);
  }
  return data as T;
}

const http = {
  get: <T>(p: string) => request<T>('GET', p),
  post: <T>(p: string, b?: unknown) => request<T>('POST', p, b),
  patch: <T>(p: string, b?: unknown) => request<T>('PATCH', p, b),
  put: <T>(p: string, b?: unknown) => request<T>('PUT', p, b),
  del: <T>(p: string) => request<T>('DELETE', p),
};

function qs(params: Record<string, unknown>): string {
  const u = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v !== undefined && v !== null && v !== '') u.set(k, String(v));
  }
  const s = u.toString();
  return s ? `?${s}` : '';
}

export interface LoginResult {
  role: SessionUser['role'];
  name: string;
  repKey: string;
}

export interface NotificationGroup {
  count: number;
  items: Array<{ id: string; company: string }>;
}
export interface Notifications {
  total: number;
  followUpsDue: NotificationGroup;
  slaRisk: NotificationGroup;
  newlyAssigned: NotificationGroup;
}
export interface SearchResults {
  leads: Array<{ id: string; company: string; contact: string; phone: string; status: string }>;
  campaigns: Array<{ name: string }>;
  quickAnswers: Array<{ id: string; question: string }>;
  users: Array<{ id: string; name: string; role: string }>;
}

export interface AuditEntry {
  id: string;
  ts: string;
  userName: string;
  role: string;
  action: string;
  detail: string;
  ip: string | null;
}

export interface DncEntry {
  id: string;
  phone: string;
  email: string;
  company: string;
  reason: string;
  addedBy: string;
  createdAt: string;
}

export interface RepScorecard {
  rep: { id: string; name: string };
  kpis: {
    leads: number;
    qualified: number;
    meetings: number;
    converted: number;
    worked: number;
    conversionPct: number;
    followUpsDue: number;
    untouched: number;
  };
  funnel: Array<{ status: string; count: number }>;
  activityMtd: { dials: number; connects: number; qls: number };
  trend: { metrics: Array<{ key: string; label: string; weekly: number[]; avg: number }> } | null;
  recentLeads: Lead[];
}

export interface SnapshotPayload {
  leads: number;
  qualified: number;
  meetings: number;
  converted: number;
  funnel: Array<{ status: string; count: number }>;
}

export const api = {
  prospectWorkspace: () => http.get<ProspectWorkspaceRecord>('/api/admin/prospect-qualification'),
  saveProspectWorkspace: (revision: number, state: ProspectWorkspace) =>
    http.put<ProspectWorkspaceRecord>('/api/admin/prospect-qualification', { revision, state }),
  // auth
  login: (pin: string) => http.post<LoginResult>('/api/auth/login', { pin }),
  logout: () => http.post('/api/auth/logout'),
  me: () => http.get<SessionUser>('/api/auth/me'),

  // general
  version: () => http.get<{ version: string }>('/api/version'),
  roster: () => http.get<Roster>('/api/roster'),
  content: () => http.get<ContentBundle>('/api/content'),
  statuses: () => http.get<{ config: StatusEntry[] }>('/api/statuses'),
  dashboard: (params: { from?: string; to?: string } = {}) =>
    http.get<DashboardStats>('/api/dashboard/stats' + qs(params)),
  notifications: () => http.get<Notifications>('/api/notifications'),
  search: (q: string) => http.get<SearchResults>('/api/search' + qs({ q })),

  // personal scratchpad notes (per rep, per scope/campaign)
  personalNote: (scope: string) =>
    http.get<{ scope: string; body: string }>('/api/notes/personal' + qs({ scope })),
  savePersonalNote: (scope: string, body: string) =>
    http.put<{ scope: string; body: string }>('/api/notes/personal', { scope, body }),

  // leads
  leads: (params: Record<string, unknown>) => http.get<Paginated<Lead>>('/api/leads' + qs(params)),
  leadCounts: () =>
    http.get<{
      total: number;
      byStatus: Record<string, number>;
      callableNow: number;
      followUpsDue: number;
      untouched: number;
      mySqls: number;
    }>('/api/leads/counts'),
  allLeads: () => http.get<{ rows: Lead[] }>('/api/leads/all'),
  getLead: (id: string) => http.get<Lead>(`/api/leads/${id}`),
  createLead: (body: Record<string, unknown>) => http.post<Lead>('/api/leads', body),
  checkDuplicate: (body: { email?: string; phone?: string; company?: string; last?: string }) =>
    http.post<{ matches: Lead[] }>('/api/leads/check-duplicate', body),
  claimNextLead: () => http.post<{ lead: Lead | null }>('/api/leads/claim-next'),
  bulkUpdateLeads: (leadIds: string[], patch: { status?: string; followUp?: string | null }) =>
    http.post<{ count: number }>('/api/leads/bulk-update', { leadIds, patch }),
  leadEvents: (id: string) =>
    http.get<{ events: Array<{ id: string; ts: string; type: string; detail: string; actor: string }> }>(
      `/api/leads/${id}/events`,
    ),
  updateLead: (id: string, body: Record<string, unknown>) =>
    http.patch<Lead>(`/api/leads/${id}`, body),
  disposition: (id: string, body: Record<string, unknown>) =>
    http.post<Lead>(`/api/leads/${id}/disposition`, body),
  qualifyLead: (id: string, body: Record<string, unknown>) =>
    http.post<Lead>(`/api/leads/${id}/qualify`, body),
  bulkReassign: (leadIds: string[], repId: string) =>
    http.post<{ count: number }>('/api/leads/bulk-reassign', { leadIds, repId }),
  rebalance: (repIds?: string[]) =>
    http.post<{ count: number }>('/api/leads/rebalance', { repIds }),
  importLeads: (body: Record<string, unknown>) =>
    http.post<{ count: number; imported: number; skipped: number }>('/api/leads/import', body),
  importDedupIndex: () =>
    http.get<{
      emails: string[];
      phones: string[];
      nameCompany: string[];
      dncEmails: string[];
      dncPhones: string[];
    }>('/api/leads/import/dedup-index'),
  duplicates: () =>
    http.get<{
      groups: Array<{ key: string; reason: string; leads: Lead[] }>;
      datasetSize: number;
    }>('/api/leads/duplicates'),
  mergeLeads: (masterId: string, mergeIds: string[], fieldSources?: Record<string, string>) =>
    http.post<{ merged: number; master: Lead }>('/api/leads/merge', { masterId, mergeIds, fieldSources }),
  dismissDuplicate: (key: string) => http.post('/api/leads/duplicates/dismiss', { key }),

  // activity
  activity: (params: Record<string, unknown>) =>
    http.get<{ rows: ActivityRecord[] }>('/api/activity' + qs(params)),
  activityTeam: (date: string) =>
    http.get<{ rows: ActivityRecord[] }>('/api/activity/team' + qs({ date })),
  activityAuto: (rep: string, date: string) =>
    http.get<{ counts: Record<string, number> }>('/api/activity/auto' + qs({ rep, date })),
  activityRollup: (month?: string) =>
    http.get<{ rows: Array<Record<string, unknown>> }>('/api/activity/rollup' + qs({ month })),
  saveActivity: (body: Record<string, unknown>) => http.post<ActivityRecord>('/api/activity', body),

  // admin
  users: () => http.get<{ rows: Rep[] }>('/api/admin/users'),
  createUser: (body: Record<string, unknown>) =>
    http.post<{ rep: Rep; pin: string }>('/api/admin/users', body),
  updateUser: (id: string, body: Record<string, unknown>) =>
    http.patch<{ rep: Rep; pin?: string }>(`/api/admin/users/${id}`, body),
  userLeadImpact: (id: string) =>
    http.get<{ total: number; touched: number; untouched: number }>(
      `/api/admin/users/${id}/lead-impact`,
    ),
  deleteUser: (id: string, mode: 'orphan' | 'purgeTouched' = 'orphan') =>
    http.del<{ ok: boolean; unassigned: number; deleted: number }>(
      `/api/admin/users/${id}?mode=${mode}`,
    ),
  saveCampaigns: (names: string[]) => http.put('/api/admin/campaigns', { names }),
  saveCampaignsFull: (
    campaigns: Array<{ name: string; family?: string; archived?: boolean }>,
  ) => http.put('/api/admin/campaigns', { campaigns }),
  campaignLeadImpact: (id: string) =>
    http.get<{ total: number }>(`/api/admin/campaigns/${id}/lead-impact`),
  setCampaignArchived: (id: string, archived: boolean) =>
    http.patch<{ ok: boolean; trashed: number }>(`/api/admin/campaigns/${id}/archive`, { archived }),
  deleteCampaign: (id: string) =>
    http.del<{ ok: boolean; deletedLeads: number }>(`/api/admin/campaigns/${id}`),
  saveScript: (campaign: string, block: Record<string, unknown>) =>
    http.put('/api/admin/scripts', { campaign, block }),
  saveQa: (items: Array<{ question: string; answer: string; tags?: string[] }>) =>
    http.put('/api/admin/qa', { items }),
  saveSettings: (meta: Record<string, unknown>) => http.put('/api/admin/settings', { meta }),
  dnc: () => http.get<{ rows: DncEntry[] }>('/api/admin/dnc'),
  addDnc: (body: { phone?: string; email?: string; company?: string; reason?: string }) =>
    http.post<{ entry: DncEntry | null }>('/api/admin/dnc', body),
  removeDnc: (id: string) => http.del(`/api/admin/dnc/${id}`),
  recycleBin: () =>
    http.get<{
      rows: Array<{
        id: string;
        leadRef: string;
        company: string;
        status: string;
        repName: string;
        deletedReason: string;
        deletedAt: string;
      }>;
    }>('/api/admin/recycle'),
  restoreDeletedLead: (id: string) => http.post(`/api/admin/recycle/${id}/restore`),
  purgeDeletedLead: (id: string) => http.del(`/api/admin/recycle/${id}`),
  purgeAllDeletedLeads: () => http.del<{ ok: boolean; purged: number }>('/api/admin/recycle'),
  audit: (params: { action?: string; q?: string; limit?: number } = {}) =>
    http.get<{ rows: AuditEntry[]; actions: string[] }>('/api/admin/audit' + qs(params)),
  logViewAs: (action: 'view-as.start' | 'view-as.stop', detail: string) =>
    http.post('/api/admin/audit/view-as', { action, detail }),

  // reports
  leaderboard: (month?: string) =>
    http.get<LeaderboardReport>('/api/reports/leaderboard' + qs({ month })),
  repScorecard: (id: string) => http.get<RepScorecard>(`/api/reports/rep/${id}`),
  campaignTrend: () =>
    http.get<{ rows: Array<{ campaign: string; month: string; leads: number; qls: number }> }>(
      '/api/reports/campaign-trend',
    ),
  snapshots: (days = 30) =>
    http.get<{ rows: Array<{ snapDate: string; payload: SnapshotPayload }> }>(
      '/api/reports/snapshots' + qs({ days }),
    ),
  saveLeaderboardSettings: (body: Partial<LeaderboardSettings>) =>
    http.put<LeaderboardSettings>('/api/reports/leaderboard/settings', body),
};
